// @flow
import { AdSlot, Grid, GridItem, Query, SeoData, SortableTable, } from '@haaretz/htz-components';
import React from 'react';
import { FelaComponent, FelaTheme, } from 'react-fela';

import type { Node, } from 'react';

import AssetsFilter from '../../components/AssetsFilter/AssetsFilter';
import PageRow from '../../components/PageRow/PageRow';
import RowItem from '../../components/RowItem/RowItem';
import MainLayout from '../../layouts/MainLayout';
import mtfFiltersQuery from '../queries/mtfFilters.graphql';
import mtfInitQuery from '../queries/mtfInitQuery.graphql';
import useAllAdUnits from '../../components/AdManager/AdUnitsProvider';
import useAdUnitPathTemplate, { createAdSlotProps, } from '../../components/AdManager/useAdUnitPathTemplate';

type Props = {
  url: {
    pathname: string,
    query: {
      section: string,
    },
    asPath: string,
  },
};

const subFilters = (fields, value, title) => ({
  title,
  value,
  fields: fields.map(field => ({
    display: field.value,
    value: { queryString: field.id, },
  })),
});

const filtersDisplay = new Map([
  [ 'mtfBranches', 'ענף', ],
  [ 'mtfStockExposure', 'חשיפה למניות', ],
  [ 'mtfCurrencyExposure', 'חשיפה למטבע', ],
  [ 'mtfManager', 'מנהל', ],
  [ 'mtfAssets', 'נכסים', ],
]);

const buildFilterObj = filters => ({
  title: 'בחר אפיק / מנהל',
  value: 'filter1',
  fields: Object.keys(filters)
    .filter(filter => filter !== '__typename' && filters[filter].length)
    .map(filter => ({
      display: filtersDisplay.get(filter) || '',
      value: {
        queryString: filter,
        subFilters: subFilters(filters[filter], 'filter2', 'בחר חשיפה: '),
      },
    })),
});

const numToString: number => string = num => num.toLocaleString('he', { minimumFractionDigits: 2, maximumFractionDigits: 2, });

function mtf({
  url: {
    query: { section, },
    asPath,
  },
}: Props): Node {
  const adUnits = useAllAdUnits();
const adUnitPathTemplate = useAdUnitPathTemplate();

  return (
    <MainLayout section={section}>
      <SeoData
        title="קרנות נאמנות - TheMarker Finance"
        description="כל המידע על  קרנות נאמנות: נתוני מסחר, נתונים בזמן אמת, גרפים חדשות ועוד באתר TheMarker Finance"
        path={asPath}
      />
      <FelaTheme>
        {theme => (
          <FelaComponent
            style={{
              extend: [
                theme.mq(
                  { until: 'l', },
                  {
                    display: 'flex',
                    flexDirection: 'column',
                  }
                ),
              ],
            }}
          >
            <PageRow miscStyles={{ order: '1', }}>
              <RowItem title="קרנות נאמנות">
                <Query query={mtfInitQuery}>
                  {({ loading, error, data, client, }) => {
                    if (loading) return null;
                    if (error) return null;

                    const preChange = (filters, callback) => (
                      client
                        .query({
                          query: mtfFiltersQuery,
                          variables: {
                            filters: [
                              {
                                section: filters[0].value,
                                subSection: filters[1].value,
                              },
                            ],
                            count: 11,
                            sortBy: 'name',
                            sortOrder: 'asc',
                            offset: 0,
                          },
                          fetchPolicy: 'network-only',
                        })
                        .then(() => {
                          callback();
                        })
                    );

                    return (
                      <AssetsFilter
                        filters={buildFilterObj(data.metadata)}
                        depth={2}
                        preChange={preChange}
                      >
                        {({ filters, }) => (
                          <SortableTable
                            miscStyles={{
                              marginTop: [
                                { until: 'l', value: '16rem', },
                                { from: 'l', value: '7rem', },
                              ],
                              tableLayout: 'auto',
                            }}
                            filters={[
                              {
                                section: filters[0].value,
                                subSection: filters[1].value,
                              },
                            ]}
                            loadMore
                            {...filters.reduce((obj, item) => {
                              // eslint-disable-next-line no-param-reassign
                              obj[item.key] = item.value.toString();
                              return obj;
                            }, {})}
                            type="mtf"
                            fragment="
                              name
                              changePercentage
                              yearlyYield
                              purchasePrice
                              redemptionPrice
                            "
                            fields={[
                              {
                                name: 'name',
                                display: 'שם הקרן',
                                sortingOrder: 'asc',
                                style: () => ({
                                  fontWeight: '700',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }),
                                value: ({ name, }) => name,
                              },
                              {
                                name: 'redemptionPrice',
                                display: 'מחיר פדיון',
                                sortingOrder: 'asc',
                                hideUntil: [ 'l', ],
                                value: ({ redemptionPrice, }) => (redemptionPrice ? `${numToString(redemptionPrice)}` : '-'),
                              },
                              {
                                name: 'purchasePrice',
                                display: 'מחיר קנייה',
                                sortingOrder: 'asc',
                                hideUntil: [ 'l', ],
                                value: ({ purchasePrice, }) => (purchasePrice ? `${numToString(purchasePrice)}` : '-'),
                              },
                              {
                                name: 'changePercentage',
                                display: 'שינוי ב-%',
                                sortingOrder: 'desc',
                                style: ({ changePercentage, }) => ({
                                  ...(
                                    changePercentage
                                      ? {
                                        color:
                                          changePercentage < 0
                                            ? theme.color('negative')
                                            : theme.color('positive'),
                                      }
                                      : {}
                                  ),
                                  direction: 'ltr',
                                  fontWeight: '700',
                                  paddingEnd: '2rem',
                                  position: 'relative',
                                  textAlign: 'start',
                                }),
                                hideUntil: [ 'l', ],
                                value: ({ changePercentage, }) => `
                                  ${changePercentage > 0 ? '' : changePercentage < 0 ? '-' : ''}
                                  ${changePercentage ? numToString(Math.abs(changePercentage)) : changePercentage}%
                                `,
                              },
                              {
                                name: 'yearlyYield',
                                display: 'תשואה שנתית',
                                sortingOrder: 'desc',
                                style: ({ yearlyYield, }) => ({
                                  ...(
                                    yearlyYield
                                      ? {
                                        color:
                                          yearlyYield < 0
                                            ? theme.color('negative')
                                            : theme.color('positive'),
                                      }
                                      : {}
                                  ),
                                  direction: 'ltr',
                                  fontWeight: '700',
                                  paddingEnd: '2rem',
                                  position: 'relative',
                                  textAlign: 'start',
                                }),
                                value: ({ yearlyYield, }) => `
                                  ${yearlyYield > 0 ? '' : yearlyYield < 0 ? '-' : ''}
                                  ${yearlyYield ? `${numToString(Math.abs(yearlyYield))}%` : '-'}
                                 `,
                              },
                            ]}
                            initialSort="name"
                            count={11}
                          />
                        )}
                      </AssetsFilter>
                    );
                  }}
                </Query>
              </RowItem>
            </PageRow>
            <PageRow miscStyles={{ order: '3', }}>
              <RowItem>
                <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.banner1'], adUnitPathTemplate)} />
              </RowItem>
            </PageRow>
            <PageRow miscStyles={{ order: '2', }}>
              <Grid>
                <GridItem
                  width={[
                    { until: 'l', value: 1, },
                    { from: 'l', value: 1 / 2, },
                  ]}
                  miscStyles={{ marginBottom: [ { until: 'l', value: '4rem', }, ], }}
                >
                  <RowItem title="ביצועי הקרנות הנמוכות בשוק">
                    <SortableTable
                      filters={[ { section: 'type', subSection: 'mtf', }, ]}
                      type="mtf"
                      fragment="
                        name
                        threeYearsYield
                      "
                      fields={[
                        {
                          name: 'name',
                          display: 'שם הקרן',
                          sortingOrder: 'asc',
                          headerStyle: {
                            width: [
                              { until: 's', value: '65%', },
                              { from: 's', until: 'l', value: '82%', },
                              { from: 'l', value: '68%', },
                            ],
                          },
                          style: () => ({
                            fontWeight: '700',
                            maxWidth: '17rem',
                            overflow: 'hidden',
                            paddingStart: '2rem',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }),
                          value: ({ name, }) => name,
                        },
                        {
                          name: 'threeYearsYield',
                          display: 'תשואה (3 שנים)',
                          sortingOrder: 'asc',
                          style: ({ threeYearsYield, }) => ({
                            ...(
                              threeYearsYield
                                ? {
                                  color:
                                    threeYearsYield < 0
                                      ? theme.color('negative')
                                      : theme.color('positive'),
                                }
                                : {}
                            ),
                            direction: 'ltr',
                            fontWeight: '700',
                            paddingEnd: '2rem',
                            position: 'relative',
                            textAlign: 'center',
                          }),
                          value: ({ threeYearsYield, }) => `
                            ${threeYearsYield > 0 ? '' : threeYearsYield < 0 ? '-' : ''}
                                  ${threeYearsYield ? numToString(Math.abs(threeYearsYield)) : threeYearsYield}%
                          `,
                        },
                      ]}
                      initialSort="threeYearsYield"
                      count={9}
                    />
                  </RowItem>
                </GridItem>
                <GridItem
                  width={[
                    { until: 'l', value: 1, },
                    { from: 'l', value: 1 / 2, },
                  ]}
                >
                  <RowItem title="ביצועי הקרנות הגבוהות בשוק">
                    <SortableTable
                      filters={[ { section: 'type', subSection: 'mtf', }, ]}
                      type="mtf"
                      fragment="
                        name
                        threeYearsYield
                      "
                      fields={[
                        {
                          name: 'name',
                          display: 'שם הקרן',
                          sortingOrder: 'asc',
                          headerStyle: {
                            width: [
                              { until: 's', value: '65%', },
                              { from: 's', until: 'l', value: '82%', },
                              { from: 'l', value: '68%', },
                            ],
                          },
                          style: () => ({
                            fontWeight: '700',
                            maxWidth: '17rem',
                            overflow: 'hidden',
                            paddingStart: '2rem',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }),
                          value: ({ name, }) => name,
                        },
                        {
                          name: 'threeYearsYield',
                          display: 'תשואה (3 שנים)',
                          sortingOrder: 'desc',
                          style: ({ threeYearsYield, }) => ({
                            ...(
                              threeYearsYield
                                ? {
                                  color:
                                    threeYearsYield < 0
                                      ? theme.color('negative')
                                      : theme.color('positive'),
                                }
                                : {}
                            ),
                            direction: 'ltr',
                            fontWeight: '700',
                            paddingEnd: '2rem',
                            position: 'relative',
                            textAlign: 'center',
                          }),
                          value: ({ threeYearsYield, }) => `
                            ${threeYearsYield > 0 ? '' : threeYearsYield < 0 ? '-' : ''}
                                  ${threeYearsYield ? numToString(Math.abs(threeYearsYield)) : threeYearsYield}%
                          `,
                        },
                      ]}
                      initialSort="threeYearsYield"
                      count={9}
                    />
                  </RowItem>
                </GridItem>
              </Grid>
            </PageRow>
            <PageRow miscStyles={{ order: '4', }}>
              <Grid>
                <GridItem
                  width={[
                    { until: 'l', value: 1, },
                    { from: 'l', value: 1 / 2, },
                  ]}
                  miscStyles={{ marginBottom: [ { until: 'l', value: '4rem', }, ], }}
                >
                  <RowItem title="הקרנות עם דמי הניהול הכי נמוכים">
                    <SortableTable
                      filters={[ { section: 'type', subSection: 'mtf', }, ]}
                      type="mtf"
                      fragment="
                        name
                        managementFee
                      "
                      fields={[
                        {
                          name: 'name',
                          display: 'שם הקרן',
                          sortingOrder: 'asc',
                          headerStyle: {
                            width: [
                              { until: 's', value: '77%', },
                              { from: 's', until: 'l', value: '88%', },
                              { from: 'l', value: '78%', },
                            ],
                          },
                          style: () => ({
                            fontWeight: '700',
                            maxWidth: '17rem',
                            overflow: 'hidden',
                            paddingStart: '2rem',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }),
                          value: ({ name, }) => name,
                        },
                        {
                          name: 'managementFee',
                          display: 'דמי ניהול',
                          sortingOrder: 'asc',
                          style: () => ({
                            textAlign: 'center',
                          }),
                          value: ({ managementFee, }) => (managementFee ? `${numToString(managementFee)}%` : '-'),
                        },
                      ]}
                      initialSort="managementFee"
                      count={9}
                    />
                  </RowItem>
                </GridItem>
                <GridItem
                  width={[
                    { until: 'l', value: 1, },
                    { from: 'l', value: 1 / 2, },
                  ]}
                >
                  <RowItem title="הקרנות עם דמי הניהול הכי גבוהים">
                    <SortableTable
                      filters={[ { section: 'type', subSection: 'mtf', }, ]}
                      type="mtf"
                      fragment="
                        name
                        managementFee
                      "
                      fields={[
                        {
                          name: 'name',
                          display: 'שם הקרן',
                          sortingOrder: 'desc',
                          headerStyle: {
                            width: [
                              { until: 's', value: '77%', },
                              { from: 's', until: 'l', value: '88%', },
                              { from: 'l', value: '78%', },
                            ],
                          },
                          style: () => ({
                            fontWeight: '700',
                            maxWidth: '17rem',
                            overflow: 'hidden',
                            paddingStart: '2rem',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }),
                          value: ({ name, }) => name,
                        },
                        {
                          name: 'managementFee',
                          display: 'דמי ניהול',
                          sortingOrder: 'desc',
                          style: () => ({
                            textAlign: 'center',
                          }),
                          value: ({ managementFee, }) => (managementFee ? `${numToString(managementFee)}%` : '-'),
                        },
                      ]}
                      initialSort="managementFee"
                      count={9}
                    />
                  </RowItem>
                </GridItem>
              </Grid>
            </PageRow>
            <PageRow miscStyles={{ order: '5', }}>
              <RowItem>
                <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.banner2'], adUnitPathTemplate)} />
              </RowItem>
            </PageRow>
            <PageRow miscStyles={{ order: '6', }}>
              <Grid>
                <GridItem
                  width={[
                    { until: 'l', value: 1, },
                    { from: 'l', value: 1 / 2, },
                  ]}
                  miscStyles={{ marginBottom: [ { until: 'l', value: '4rem', }, ], }}
                >
                  <RowItem title="הקרנות עם היקף הנכסים הגדול ביותר">
                    <SortableTable
                      filters={[ { section: 'type', subSection: 'mtf', }, ]}
                      type="mtf"
                      fragment="
                        name
                        assetsUnderManagement
                      "
                      fields={[
                        {
                          name: 'name',
                          display: 'שם הקרן',
                          sortingOrder: 'asc',
                          headerStyle: {
                            width: [
                              { until: 's', value: '65%', },
                              { from: 's', until: 'l', value: '82%', },
                              { from: 'l', value: '68%', },
                            ],
                          },
                          style: () => ({
                            fontWeight: '700',
                            maxWidth: '17rem',
                            overflow: 'hidden',
                            paddingStart: '2rem',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }),
                          value: ({ name, }) => name,
                        },
                        {
                          name: 'assetsUnderManagement',
                          display: 'סך נכסים (מ\' ש\')',
                          sortingOrder: 'desc',
                          style: () => ({
                            textAlign: 'center',
                          }),
                          value: ({ assetsUnderManagement, }) => numToString(assetsUnderManagement || ''),
                        },
                      ]}
                      initialSort="assetsUnderManagement"
                      count={9}
                    />
                  </RowItem>
                </GridItem>
                <GridItem
                  width={[
                    { until: 'l', value: 1, },
                    { from: 'l', value: 1 / 2, },
                  ]}
                >
                  <RowItem title="הקרנות עם היקף הנכסים הקטן ביותר">
                    <SortableTable
                      filters={[ { section: 'type', subSection: 'mtf', }, ]}
                      type="mtf"
                      fragment="
                        name
                        assetsUnderManagement
                      "
                      fields={[
                        {
                          name: 'name',
                          display: 'שם הקרן',
                          sortingOrder: 'desc',
                          headerStyle: {
                            width: [
                              { until: 's', value: '65%', },
                              { from: 's', until: 'l', value: '82%', },
                              { from: 'l', value: '68%', },
                            ],
                          },
                          style: () => ({
                            fontWeight: '700',
                            maxWidth: '17rem',
                            overflow: 'hidden',
                            paddingStart: '2rem',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }),
                          value: ({ name, }) => name,
                        },
                        {
                          name: 'assetsUnderManagement',
                          display: 'סך נכסים (מ\' ש\')',
                          sortingOrder: 'asc',
                          style: () => ({
                            textAlign: 'center',
                          }),
                          value: ({ assetsUnderManagement, }) => numToString(assetsUnderManagement || ''),
                        },
                      ]}
                      initialSort="assetsUnderManagement"
                      count={9}
                    />
                  </RowItem>
                </GridItem>
              </Grid>
            </PageRow>
          </FelaComponent>
        )}
      </FelaTheme>
    </MainLayout>
  );
}

export default mtf;
